import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, TrendingUp, Zap, Clock, Download, Link2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RANGES = [
  { key: 7, label: "7 days" },
  { key: 14, label: "14 days" },
  { key: 30, label: "30 days" },
  { key: 90, label: "90 days" }
];

export default function AnalyticsPanel({ authHeaders }) {
  const [days, setDays] = useState(7);
  const [speed, setSpeed] = useState(null);
  const [combos, setCombos] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setBusy(true);
    Promise.all([
      axios.get(`${API}/admin/analytics/cook-speed`, { headers: authHeaders, params: { days } }),
      axios.get(`${API}/admin/analytics/combos`, { headers: authHeaders, params: { days: Math.max(days, 30) } })
    ])
      .then(([s, c]) => { setSpeed(s.data); setCombos(c.data); })
      .finally(() => setBusy(false));
  }, [days, authHeaders]);

  const download = async (kind) => {
    const params = kind === "combos" ? { days: Math.max(days, 30) } : { days };
    const res = await axios.get(`${API}/admin/analytics/${kind}.csv`, {
      headers: authHeaders, params, responseType: "blob",
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mamamina-${kind}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const items = speed?.items || [];
  const worstAvg = items[0]?.avg_minutes || 0;

  return (
    <div className="space-y-6">
      {/* Header + range selectors */}
      <div className="p-5 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)] flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1">
          <div className="section-label mb-2 inline-flex items-center gap-2">
            <TrendingUp size={14} /> Cook Speed & Combo Insights
          </div>
          <p className="text-sm text-[var(--mm-cream-muted)]">
            Two data-driven views: which dishes slow the line, and which dishes get ordered together (=&gt; bundle candidates).
          </p>
        </div>
        <div className="flex gap-1">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setDays(r.key)}
              data-testid={`analytics-range-${r.key}`}
              className={`text-[0.65rem] tracking-[0.22em] uppercase px-3 py-2 border transition-all ${
                days === r.key
                  ? "bg-[var(--mm-accent)] text-[var(--mm-bg)] border-[var(--mm-accent)]"
                  : "border-[var(--mm-line)] text-[var(--mm-cream-muted)] hover:border-[var(--mm-accent)] hover:text-[var(--mm-accent)]"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {busy ? (
        <div className="py-16 text-center"><Loader2 size={24} className="mx-auto text-[var(--mm-accent)] animate-spin" /></div>
      ) : (
        <>
          {/* Cook Speed */}
          <Section
            title="Cook Speed"
            subtitle="Average prep window per dish. Slowest at the top."
            onDownload={() => download("cook-speed")}
          >
            {!speed || speed.orders_considered === 0 ? (
              <Empty text="No completed cook cycles yet in this window." />
            ) : (
              <>
                <div className="grid grid-cols-3 gap-4">
                  <StatCard label="Orders analysed" value={speed.orders_considered} />
                  <StatCard label="Overall avg prep" value={`${speed.overall_avg_minutes}m`} />
                  <StatCard label="Units sold" value={speed.total_units_sold} />
                </div>
                <div className="overflow-x-auto mt-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[0.66rem] tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] border-b border-[var(--mm-line)]">
                        <th className="py-3 pr-4">Dish</th>
                        <th className="py-3 pr-4 text-right">Orders</th>
                        <th className="py-3 pr-4 text-right">Units</th>
                        <th className="py-3 pr-4 text-right">Avg</th>
                        <th className="py-3 pr-4 text-right">P90</th>
                        <th className="py-3 pr-4 text-right">Max</th>
                        <th className="py-3 pl-2 w-40">Relative</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((it, idx) => {
                        const pct = worstAvg > 0 ? Math.round((it.avg_minutes / worstAvg) * 100) : 0;
                        const bad = idx === 0 && items.length > 1;
                        const good = idx === items.length - 1 && items.length > 1;
                        return (
                          <tr key={it.lookup_key} className="border-b border-[var(--mm-line)]/60 text-[var(--mm-cream)]">
                            <td className="py-3 pr-4 font-medium">
                              <div className="inline-flex items-center gap-2">
                                {bad && <Clock size={14} className="text-red-400" />}
                                {good && <Zap size={14} className="text-[var(--mm-accent)]" />}
                                {it.name}
                              </div>
                            </td>
                            <td className="py-3 pr-4 text-right text-[var(--mm-cream-muted)]">{it.orders}</td>
                            <td className="py-3 pr-4 text-right text-[var(--mm-cream-muted)]">{it.units_sold}</td>
                            <td className="py-3 pr-4 text-right font-semibold">{it.avg_minutes}m</td>
                            <td className="py-3 pr-4 text-right text-[var(--mm-cream-muted)]">{it.p90_minutes}m</td>
                            <td className="py-3 pr-4 text-right text-[var(--mm-cream-muted)]">{it.max_minutes}m</td>
                            <td className="py-3 pl-2">
                              <div className="h-2 bg-[var(--mm-line)] w-full">
                                <div
                                  className={`h-full ${bad ? "bg-red-400" : good ? "bg-[var(--mm-accent)]" : "bg-[var(--mm-accent)]/60"}`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Section>

          {/* Combos */}
          <Section
            title="Dish Combos"
            subtitle="Pairs ordered together in the same paid order (min 30-day window)."
            onDownload={() => download("combos")}
          >
            {!combos || combos.combos?.length === 0 ? (
              <Empty text="Not enough multi-item orders yet. Once diners start pairing dishes, combos surface here." />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[0.66rem] tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] border-b border-[var(--mm-line)]">
                      <th className="py-3 pr-4">Pair</th>
                      <th className="py-3 pr-4 text-right">Times together</th>
                      <th className="py-3 pr-4 text-right">Avg order value</th>
                      <th className="py-3 pr-4 text-right">Affinity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(combos?.combos || []).map((c, i) => (
                      <tr key={c.item_a + c.item_b} className="border-b border-[var(--mm-line)]/60 text-[var(--mm-cream)]">
                        <td className="py-3 pr-4">
                          <div className="inline-flex items-center gap-2">
                            <Link2 size={14} className={i < 3 ? "text-[var(--mm-accent)]" : "text-[var(--mm-cream-muted)]"} />
                            <span className="font-medium">{c.item_a_name}</span>
                            <span className="text-[var(--mm-cream-muted)]">+</span>
                            <span className="font-medium">{c.item_b_name}</span>
                          </div>
                        </td>
                        <td className="py-3 pr-4 text-right font-semibold text-[var(--mm-accent)]">{c.times_together}</td>
                        <td className="py-3 pr-4 text-right">${c.avg_order_value_aud.toFixed(2)}</td>
                        <td className="py-3 pr-4 text-right text-[var(--mm-cream-muted)]" title="how often B accompanies A">
                          {Math.round(c.affinity * 100)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </>
      )}
    </div>
  );
}

function Section({ title, subtitle, onDownload, children }) {
  return (
    <div className="border border-[var(--mm-line)] bg-[var(--mm-bg-alt)]/60 p-5">
      <div className="flex items-start justify-between gap-4 mb-4 pb-4 border-b border-[var(--mm-line)]/60">
        <div>
          <div className="font-display italic text-3xl text-[var(--mm-cream)]">{title}</div>
          <div className="text-sm text-[var(--mm-cream-muted)] mt-1">{subtitle}</div>
        </div>
        <button
          onClick={onDownload}
          data-testid={`analytics-csv-${title.toLowerCase().replace(/\s+/g, "-")}`}
          className="btn-outline inline-flex items-center gap-2 shrink-0"
        >
          <Download size={14} /> CSV
        </button>
      </div>
      {children}
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="p-5 border border-[var(--mm-line)] bg-[var(--mm-bg)]">
      <div className="text-xs tracking-[0.24em] uppercase text-[var(--mm-cream-muted)] mb-2">{label}</div>
      <div className="font-display text-3xl text-[var(--mm-accent)]">{value}</div>
    </div>
  );
}

function Empty({ text }) {
  return <div className="py-10 text-center text-[var(--mm-cream-muted)] font-light">{text}</div>;
}
